// LeftOrderTicketCard.tsx
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaidIcon from '@mui/icons-material/Paid';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CustomAvatar from 'components/ui/customAvatar';
import { t } from 'i18next';
import { MissingPhoto, toEnumPriority } from 'Interfaces/IGlobalTypeConfig';
import * as React from 'react';
import {
  OrderPostCompanyViewModel,
  OrderPostProductViewModel,
  OrderPostTicketContactInfoViewModel,
  SingleOrderPostTicketViewModel,
} from '../ViewModels/OrderPostViewModels';
import { IOrderCompanyViewModel } from 'Interfaces/IOrderConfig';

export function mapVendorToOrderPostCompany(
  v: IOrderCompanyViewModel | null | undefined
): OrderPostCompanyViewModel | null {
  if (!v) return null;
  return {
    id: v.id,
    name: v.name,
    organizationNumber: v.organizationNumber ?? '',
    onHold: !!v.onHold,
    onHoldNote: v.onHoldNote ?? null,
    priority: toEnumPriority(v.priority),
    url: v.url ?? '', // legg til i IOrderCompanyViewModel hvis du har det
  };
}
type AnyVendor = {
  id: string;
  name: string;
  organizationNumber?: string | null;
  onHold?: boolean;
  onHoldNote?: string | null;
  priority?: unknown; // kan være string/number/EnumPriority
  url?: string;
};

export function toOrderPostCompany(v: AnyVendor): OrderPostCompanyViewModel {
  console.log('toOrderPostCompany - input:', v);
  return {
    id: v.id,
    name: v.name,
    organizationNumber: v.organizationNumber ?? '',
    onHold: !!v.onHold,
    onHoldNote: v.onHoldNote ?? null,
    priority: toEnumPriority(v.priority),
    url: v.url ?? '',
  };
}

function getProfitColor(total: number) {
  if (total < 0) return 'warning.darkest';
  if (total === 0) return 'primary.darkest';
  return 'success.darkest';
}

export function computeMargin(p: OrderPostProductViewModel) {
  const unit = p.unitPrice ?? 0;
  const retail = p.retailPrice ?? 0;
  const qty = p.orderedQuantity ?? 0;

  const perUnit = retail - unit;
  const pct = retail > 0 ? (perUnit / retail) * 100 : 0;
  const total = perUnit * qty;

  return { perUnit, pct, total, unit, retail };
}

// Hvor mye av behovet er bestilt?
export type FillState = 'none' | 'partial' | 'full' | 'over' | 'invalid';

export function getFillState(
  ordered: number | undefined,
  alreadyOrdered: number | undefined,
  qty: number | undefined
): FillState {
  const o = Math.max(0, (ordered ?? 0) + (alreadyOrdered ?? 0));

  const q = Math.max(0, qty ?? 0);

  if (q === 0 && o === 0) return 'none';
  if (q === 0 && o > 0) return 'over'; // uvanlig, men håndteres
  if (o === 0) return 'none';
  if (o === q) return 'full';
  if (o > q) return 'over';
  if (o < q) return 'partial';

  return 'invalid';
}

// Bakgrunnsfarge for kort/rad basert på status
export function getBgColorForFill(state: FillState): string {
  switch (state) {
    case 'full':
      return 'success.dark'; // alt bestilt
    case 'partial':
      return 'warning.dark'; // delvis bestilt
    case 'over':
      return 'third.dark'; // bestilt mer enn behov
    case 'none':
      return 'primary.main'; // nøytral
    default:
      return 'primary.main';
  }
}

// (valgfritt) Kantlinje / chip-farge om du vil
export function getChipColorForFill(
  state: FillState
): 'default' | 'success' | 'warning' | 'error' {
  switch (state) {
    case 'full':
      return 'success';
    case 'partial':
      return 'warning';
    case 'over':
      return 'error';
    case 'none':
      return 'default';
    default:
      return 'default';
  }
}

type Props = {
  item: SingleOrderPostTicketViewModel;
  onPickProduct?: (product: any) => void;
  getOrderedCount: (productId: string) => number;
};

type SelectedOrderProductCardProps = {
  product: OrderPostProductViewModel;
  t: (k: string) => string;
  getOrderedCount: (id: string) => number;
  setOrderedCount: (id: string, n: number) => void;
  updateSelected: (
    id: string,
    patch: Partial<OrderPostProductViewModel>
  ) => void;
  onRemove: () => void;
};

export type GroupedProduct = {
  id: string; // concatenated owner ids
  urlKey: string; // vendorProductUrl
  name: string;
  imageUrl?: string;
  vendor?: { name?: string; url?: string };
  quantity: number; // sum of quantities
  orderedQuantity: number; // sum of orderedQuantities
  unitPrice: number; // canonical price for the group
  retailPrice: number;
  owners: OrderPostTicketContactInfoViewModel[]; // deduped by owner.id if desired
};

export const LeftOrderTicketCard: React.FC<Props> = ({
  item,
  onPickProduct,
  getOrderedCount,
}) => {
  console.log('LeftOrderTicketCard - item', item);
  return (
    <Paper
      sx={{
        mb: 1,
        borderRadius: 3,
        boxShadow: 3,
        backgroundColor: 'primary.light',
        borderLeftColor: 'success.light',
        borderLeftWidth: 15,
        borderRightWidth: 5,
        borderLeftStyle: 'solid',
        borderRightStyle: 'solid',
        pl: 1,
        pr: 1,
        pt: -1,
        pb: 0.1,
      }}
    >
      {/* COMPANY INFO */}
      <Grid
        container
        spacing={0}
        alignItems='center'
        justifyContent='space-between'
        sx={{}}
      >
        {/* COMPANY IMAGE */}
        <Grid item sx={{}}>
          {item.companyInfo?.url && item.companyInfo.url !== '' ? (
            <img
              src={item.companyInfo?.url || MissingPhoto.COMPANY_URL}
              style={{
                padding: 0,
                width: 120,
                height: 60,
                borderRadius: 3,
                objectFit: 'scale-down',
              }}
              alt={'previewUrl'}
            />
          ) : (
            <CustomAvatar
              avatarName={item.companyInfo?.name || ''}
              imgWidth={160}
              imgHeight={96}
            />
          )}
        </Grid>
        {/* COMPANY NAME */}
        <Grid item sx={{ flexGrow: 1, textAlign: 'left' }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography
              sx={{
                fontSize: 24,
                flexGrow: 1,
                fontWeight: 'bold',
                letterSpacing: 2,
              }}
            >
              {item.companyInfo?.name}
            </Typography>
            <Typography
              sx={{ fontSize: 20, fontWeight: 'bold', letterSpacing: 2 }}
            >
              {item.ticketNo}
            </Typography>
          </Stack>
          {/* CONTACT INFO */}
          <Grid
            container
            spacing={1}
            alignItems='center'
            justifyContent='space-between'
          >
            <Grid item sx={{}}>
              {item.contact?.url && item.contact.url !== '' ? (
                <img
                  src={item.contact?.url || MissingPhoto.EMPLOYEE_URL}
                  style={{
                    padding: 0,
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    objectFit: 'scale-down',
                  }}
                  alt={'previewUrl'}
                />
              ) : (
                <CustomAvatar
                  avatarName={item.contact?.name || ''}
                  imgWidth={64}
                  imgHeight={64}
                />
              )}
            </Grid>
            <Grid item sx={{ flexGrow: 1, textAlign: 'left' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography sx={{ fontSize: 20, flexGrow: 1 }}>
                  {item.contact?.name}
                </Typography>
                <Typography sx={{ fontSize: 20 }}>
                  {item.contact?.phone}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 20 }}>
                {item.contact?.email}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ my: 1 }} />
      {/* ORDER INFO */}

      <Grid item xs={12} sx={{ mr: 1, ml: 1 }}>
        {/* Product INFO */}
        {item.ticketProducts.map((prod, index) => {
          const fillState = getFillState(
            prod.orderedQuantity,
            prod.alreadyOrdered,
            prod.quantity
          );
          const bg = getBgColorForFill(fillState);
          const chipColor = getChipColorForFill(fillState);
          return (
            <ButtonBase
              key={index}
              onClick={() => onPickProduct && onPickProduct(prod)}
              sx={{
                width: '100%',
                mb: 1,
                borderRadius: 3,
                border: 1,
                borderColor: bg,
                p: 0,
                textAlign: 'left',
                display: 'block',
                backgroundColor: bg,
                '&:hover': {
                  backgroundColor: bg,
                  opacity: 0.8,
                  cursor: 'pointer',
                  border: 1,
                  borderColor: 'primary.contrastText',
                },
              }}
            >
              <Grid
                container
                spacing={1}
                alignItems='center'
                justifyContent='space-between'
                key={index}
                sx={{
                  backgroundColor: 'transparent',
                }}
              >
                {/* Product IMAGE */}
                <Grid item sx={{}}>
                  {prod.url !== '' ? (
                    <img
                      src={prod.url || MissingPhoto.PRODUCT_URL}
                      style={{
                        borderRadius: 5,
                        border: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        overflow: 'hidden',
                        userSelect: 'none',
                        width: 120,
                        height: 120,
                        objectFit: 'scale-down',
                      }}
                      alt={'previewUrl'}
                    />
                  ) : (
                    <CustomAvatar
                      avatarName={prod?.name || ''}
                      imgWidth={120}
                      imgHeight={120}
                    />
                  )}
                </Grid>
                {/* Owner NAME */}
                <Grid item sx={{ flexGrow: 1, textAlign: 'left' }}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography
                      sx={{
                        fontSize: 24,
                        flexGrow: 1,
                        fontWeight: 'bold',
                        letterSpacing: 2,
                      }}
                    >
                      {prod?.name}
                    </Typography>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 'bold',
                          letterSpacing: 2,
                        }}
                      >
                        {prod?.vendor?.name}
                      </Typography>
                      {prod?.vendor?.url && prod?.vendor.url !== '' ? (
                        <img
                          src={prod?.vendor?.url || MissingPhoto.COMPANY_URL}
                          style={{
                            borderRadius: 5,
                            border: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                            userSelect: 'none',
                            width: 80,
                            height: 40,
                            objectFit: 'scale-down',
                          }}
                          alt={'previewUrl'}
                        />
                      ) : (
                        <CustomAvatar
                          avatarName={prod?.vendor?.name || ''}
                          imgWidth={80}
                          imgHeight={40}
                        />
                      )}
                    </Stack>
                  </Stack>
                  {/* CONTACT INFO */}
                  <Grid
                    container
                    spacing={1}
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{}}
                  >
                    <Grid item sx={{}}>
                      {prod.owner?.url && prod.owner.url !== '' ? (
                        <img
                          src={prod.owner?.url || MissingPhoto.EMPLOYEE_URL}
                          style={{
                            padding: 0,
                            width: 80,
                            height: 80,
                            borderRadius: 3,
                            objectFit: 'scale-down',
                          }}
                          alt={'previewUrl'}
                        />
                      ) : (
                        <CustomAvatar
                          avatarName={prod.owner?.name || ''}
                          imgWidth={80}
                          imgHeight={80}
                        />
                      )}
                    </Grid>
                    <Grid item sx={{ flexGrow: 1, textAlign: 'left' }}>
                      <Stack direction='row' spacing={0}>
                        <Typography
                          sx={{
                            fontSize: 18,
                            flexGrow: 1,
                            maxWidth: '62%',
                            minWidth: '62%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {prod.owner?.name}
                        </Typography>
                        <Stack
                          direction='row'
                          spacing={0}
                          sx={{
                            display: 'flex',
                            maxWidth: '38%',
                            minWidth: '38%',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 18,
                              width: 120,
                              display: 'flex',
                              alignItems: 'right',
                              textAlign: 'right',
                              justifyContent: 'right',
                            }}
                          >
                            {t('bluu.quantity')}
                          </Typography>
                          <Stack
                            direction='row'
                            spacing={0}
                            sx={{ flexGrow: 1 }}
                          >
                            <Chip
                              label={prod.alreadyOrdered}
                              color={chipColor}
                              size='small'
                              sx={{ height: 23, ml: 1 }}
                            />
                            <Typography
                              sx={{
                                fontSize: 18,
                                flexGrow: 1,
                                alignItems: 'right',
                                textAlign: 'right',
                                pr: 0.5,
                              }}
                            >
                              {prod.orderedQuantity}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 18,
                                width: '14%',
                                alignItems: 'center',
                                textAlign: 'center',
                              }}
                            >
                              {'/'}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 18,
                                display: 'flex',
                                alignItems: 'right',
                                textAlign: 'right',
                                justifyContent: 'right',
                              }}
                            >
                              {prod.quantity}
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{
                              fontSize: 18,
                              width: 40,
                              alignItems: 'right',
                              textAlign: 'right',
                              pr: 0.5,
                            }}
                          >
                            {t('bluu.quantityAbbr').toLowerCase()}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack direction='row' spacing={0}>
                        <Typography
                          sx={{
                            flexGrow: 1,
                            fontSize: 18,
                            maxWidth: '62%',
                            minWidth: '62%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {prod.owner?.email}
                        </Typography>
                        <Stack
                          direction='row'
                          spacing={0}
                          sx={{
                            maxWidth: '38%',
                            minWidth: '38%',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 18,
                              width: 120,
                              display: 'flex',
                              alignItems: 'right',
                              textAlign: 'right',
                              justifyContent: 'right',
                            }}
                          >
                            {t('bluu.costPrice')}
                          </Typography>
                          <Typography
                            sx={{
                              flexGrow: 1,
                              fontSize: 18,
                              alignItems: 'right',
                              textAlign: 'right',
                            }}
                          >
                            {prod.unitPrice.toFixed(2)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 18,
                              width: 40,
                              alignItems: 'right',
                              textAlign: 'right',
                              pr: 0.5,
                            }}
                          >
                            {t('bluu.currency').toLowerCase()}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack direction='row' spacing={0}>
                        <Typography
                          sx={{
                            fontSize: 18,
                            flexGrow: 1,
                            maxWidth: '62%',
                            minWidth: '62%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {prod.owner?.phone}
                        </Typography>
                        <Stack
                          direction='row'
                          spacing={0}
                          sx={{
                            maxWidth: '38%',
                            minWidth: '38%',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 18,
                              width: 120,
                              display: 'flex',
                              alignItems: 'right',
                              textAlign: 'right',
                              justifyContent: 'right',
                            }}
                          >
                            {t('bluu.retailPrice')}
                          </Typography>
                          <Typography
                            sx={{
                              flexGrow: 1,
                              fontSize: 18,
                              alignItems: 'right',
                              textAlign: 'right',
                              color:
                                prod.unitPrice > prod.retailPrice
                                  ? 'error.main'
                                  : 'primary.contrastText',
                            }}
                          >
                            {prod.retailPrice.toFixed(2)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 18,
                              width: 40,
                              alignItems: 'right',
                              textAlign: 'right',
                              pr: 0.5,
                            }}
                          >
                            {t('bluu.currency').toLowerCase()}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ pl: 1, pb: 0.5, minWidth: 0 }}>
                  <Grid container alignItems='center' wrap='nowrap'>
                    {/* Venstre: lenken (tar tilgjengelig bredde) */}
                    <Grid item xs zeroMinWidth>
                      <Tooltip title={prod.vendorProductUrl}>
                        <a
                          href={prod.vendorProductUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{
                            fontSize: 18,
                            fontWeight: 'normal',
                            letterSpacing: 1,
                            paddingLeft: 4,
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textDecoration: 'underline',
                            color:
                              prod.vendorProductUrl.length > 0
                                ? 'lightblue'
                                : 'lightgray',
                          }}
                        >
                          {prod.vendorProductUrl.length > 0
                            ? prod.vendorProductUrl
                            : t('order.orderPost.noUrl')}
                        </a>
                      </Tooltip>
                    </Grid>

                    {/* Høyre: margin + fargekodet retail */}
                    <Grid item sx={{ pl: 1 }}>
                      {(() => {
                        const { pct, total } = computeMargin(prod);
                        const qty = prod.orderedQuantity ?? 0;

                        // Nøytral når 0 bestilt
                        const neutral = qty === 0;
                        const profitColor = neutral
                          ? 'text.secondary'
                          : getProfitColor(total); // error/warning/success når qty > 0

                        return (
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                          >
                            <Divider orientation='vertical' flexItem />

                            {/* Prosent – nøytral ved 0 bestilt */}
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: neutral ? 'text.secondary' : profitColor,
                              }}
                            >
                              ({pct.toFixed(0)}%)
                            </Typography>

                            {/* Total – nøytral ved 0 bestilt */}
                            <Tooltip title={t('Orders.OrderPosts.totalProfit')}>
                              <Chip
                                size='small'
                                variant={neutral ? 'outlined' : 'filled'}
                                sx={
                                  neutral
                                    ? { color: 'text.secondary' }
                                    : {
                                        bgcolor: profitColor,
                                        color: 'primary.contrastText',
                                        fontWeight: 600,
                                      }
                                }
                                label={`${total.toFixed(2)} ${t(
                                  'bluu.currency'
                                ).toLowerCase()}`}
                              />
                            </Tooltip>
                          </Stack>
                        );
                      })()}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </ButtonBase>
          );
        })}
      </Grid>
    </Paper>
  );
};

export const SelectedOrderProductCard = ({
  product,
  t,
  getOrderedCount,
  setOrderedCount,
  updateSelected,
  onRemove,
}: SelectedOrderProductCardProps) => {
  const ordered = getOrderedCount(product.id);
  const available = Math.max(0, (product.quantity ?? 0) - ordered);

  return (
    <Card
      elevation={3}
      sx={{ mb: 2, borderRadius: 3, bgcolor: 'primary.main' }}
    >
      <CardHeader
        avatar={
          product.url ? (
            <Stack direction='row' spacing={1} alignItems='center'>
              <Box
                component='img'
                src={product.url || MissingPhoto.PRODUCT_URL}
                alt='prod'
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  objectFit: 'contain',
                  bgcolor: 'primary.light',
                }}
              />
            </Stack>
          ) : (
            <CustomAvatar
              avatarName={product?.name || ''}
              imgWidth={56}
              imgHeight={56}
            />
          )
        }
        title={
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {product?.name}
          </Typography>
        }
        subheader={
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ minWidth: 0 }}
          >
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 'bold',
                letterSpacing: 2,
              }}
            >
              {product?.vendor?.name}
            </Typography>

            {product?.vendor?.url && product?.vendor.url !== '' ? (
              <img
                src={product?.vendor?.url || MissingPhoto.COMPANY_URL}
                style={{
                  padding: 0,
                  width: 80,
                  height: 40,
                  borderRadius: 3,
                  objectFit: 'scale-down',
                }}
                alt={'previewUrl'}
              />
            ) : (
              <CustomAvatar
                avatarName={product?.vendor?.name || ''}
                imgWidth={80}
                imgHeight={40}
              />
            )}
          </Stack>
        }
        action={
          <IconButton aria-label='remove' onClick={onRemove}>
            <DeleteOutlineIcon />
          </IconButton>
        }
        sx={{ pb: 0.5 }}
      />
      <CardContent sx={{ pt: 1.5 }}>
        <Grid container spacing={2} alignItems='center'>
          {/* Owner */}
          <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              {product.owner?.url ? (
                <Box
                  component='img'
                  src={product.owner?.url || MissingPhoto.EMPLOYEE_URL}
                  alt='owner'
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    objectFit: 'contain',
                    bgcolor: 'primary.light',
                  }}
                />
              ) : (
                <CustomAvatar
                  avatarName={product.owner?.name || ''}
                  imgWidth={48}
                  imgHeight={48}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.owner?.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    opacity: 0.8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.owner?.email}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    opacity: 0.8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.owner?.phone}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Fields */}
          <Grid item xs={12} md={8}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              {/* Quantity */}
              <TextField
                label={t('bluu.quantity')}
                type='number'
                size='small'
                value={ordered}
                onChange={(e) => {
                  const v = Number(e.target.value) || 0;
                  setOrderedCount(
                    product.id,
                    Math.min(Math.max(0, v), product.quantity ?? 0)
                  );
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <IconButton
                        size='small'
                        onClick={() =>
                          setOrderedCount(product.id, Math.max(0, ordered - 1))
                        }
                      >
                        <RemoveIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={() =>
                          setOrderedCount(
                            product.id,
                            Math.min(product.quantity ?? 0, ordered + 1)
                          )
                        }
                      >
                        <AddIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, max: product.quantity ?? 0 },
                }}
                sx={{
                  width: { xs: '100%', sm: 190 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'primary.light',
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                    appearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                    {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                }}
              />

              {/* Cost price */}
              <TextField
                label={t('bluu.costPrice')}
                type='number'
                size='small'
                value={product.unitPrice}
                onChange={(e) =>
                  updateSelected(product.id, {
                    unitPrice: Number(e.target.value) || 0,
                  })
                }
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isNaN(n))
                    updateSelected(product.id, {
                      unitPrice: Number(n.toFixed(2)),
                    });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position='start'
                      sx={{ color: 'text.secondary' }}
                    >
                      <PaidIcon fontSize='small' />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position='end'
                      sx={{ color: 'text.secondary', mr: 0.5 }}
                    >
                      {t('bluu.currency').toUpperCase()}
                    </InputAdornment>
                  ),
                  inputProps: { step: '0.01', min: 0, inputMode: 'decimal' },
                }}
                sx={{
                  width: { xs: '100%', sm: 220 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'primary.light',
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                    appearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                    {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                }}
              />
            </Stack>

            {/* URL editor + preview */}
            <Box sx={{ mt: 1.5, minWidth: 0 }}>
              <TextField
                label={t('Orders.OrderPosts.vendorUrl')}
                size='small'
                value={product.vendorProductUrl || ''}
                onChange={(e) =>
                  updateSelected(product.id, {
                    vendorProductUrl: e.target.value,
                  })
                }
                fullWidth
              />
              {product.vendorProductUrl ? (
                <Tooltip title={product.vendorProductUrl}>
                  <a
                    href={product.vendorProductUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      marginTop: 0.5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 13,
                    }}
                  >
                    {product.vendorProductUrl}
                    <OpenInNewIcon fontSize='inherit' sx={{ ml: 0.5 }} />
                  </a>
                </Tooltip>
              ) : null}
            </Box>
          </Grid>
        </Grid>

        {/* Status chips */}
        <Stack direction='row' spacing={1} sx={{ mt: 1.5 }}>
          <Chip
            label={`${ordered} ${t('order.orderPost.orderedLabel')}`}
            color={ordered > 0 ? 'error' : 'default'}
            variant={ordered > 0 ? 'filled' : 'outlined'}
            size='small'
          />
          <Chip
            label={`${available} ${t('order.orderPost.availableLabel')}`}
            variant='outlined'
            size='small'
          />
          <Typography sx={{ ml: 'auto', fontSize: 13, opacity: 0.8 }}>
            {t('bluu.retailPrice')}:&nbsp;
            <Typography
              component='span'
              sx={{
                fontWeight: 600,
                color:
                  product.unitPrice > product.retailPrice
                    ? 'error.main'
                    : 'inherit',
              }}
            >
              {product.retailPrice.toFixed(2)}{' '}
              {t('bluu.currency').toLowerCase()}
            </Typography>
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export function groupByProductUrl(
  items: OrderPostProductViewModel[]
): GroupedProduct[] {
  const map = new Map<string, GroupedProduct>();

  for (const p of items) {
    const key = p.vendorProductUrl?.trim();
    if (!key) continue;

    const existing = map.get(key);
    const ownerEntry: OrderPostTicketContactInfoViewModel = {
      id: p.owner?.id || `${p.owner?.email ?? p.owner?.name ?? 'unknown'}`,
      name: p.owner?.name ?? 'Unknown',
      email: p.owner?.email ?? '',
      phone: p.owner?.phone ?? '',
      url: p.owner?.url ?? '',
    };

    if (!existing) {
      map.set(key, {
        id: ownerEntry.id,
        urlKey: key,
        name: p.name,
        imageUrl: p.url,
        quantity: p.quantity, // initial quantity
        orderedQuantity: p.orderedQuantity, // initial orderedQuantity
        unitPrice: p.unitPrice,
        retailPrice: p.retailPrice,
        owners: [ownerEntry],
      });
    } else {
      // merge owner quantities
      const i = existing.owners.findIndex((o) => o.id === ownerEntry.id);
      if (i >= 0) {
        // already exists, update quantities
        existing.owners[i] = ownerEntry; // update owner info if needed
      } else {
        existing.owners.push(ownerEntry);
      }
    }
  }

  return Array.from(map.values());
}
